import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import ConvertTicketDialog from "@/components/ConvertTicketDialog";
import SavTicketTimeline from "@/components/SavTicketTimeline";
import SavRatingBadge from "@/components/SavRatingBadge";
import { Warning, Phone, EnvelopeSimple, MapPin, ArrowRight, CheckCircle, Clock, Info, LightbulbFilament, Fire, ArrowsClockwise, UserCircle, FilePdf, CaretDown, CaretUp, Trash, CheckSquare, Square } from "@phosphor-icons/react";
import { emitSavUpdated } from "@/lib/useSavUnreadCount";

const STATUS_META = {
  open: { label: "Ouvert", color: "#EF4444", Icon: Warning },
  acknowledged: { label: "Vu", color: "#F59E0B", Icon: Clock },
  in_progress: { label: "En cours", color: "#005CFF", Icon: LightbulbFilament },
  resolved: { label: "Résolu", color: "#10B981", Icon: CheckCircle },
  closed: { label: "Clos", color: "#6B7280", Icon: CheckCircle },
};
const PRIO_META = {
  low: { label: "Basse", color: "#94A3B8" },
  normal: { label: "Normale", color: "#7CB2FF" },
  high: { label: "Haute", color: "#F59E0B" },
  critical: { label: "Critique", color: "#EF4444" },
};

const FILTERS = [
  { key: "active", label: "À traiter" },
  { key: "open", label: "Ouverts" },
  { key: "in_progress", label: "En cours" },
  { key: "resolved", label: "Résolus" },
  { key: "closed", label: "Clos" },
];

/**
 * Alertes / SAV — combines local urgent chantiers with external SAV tickets
 * pushed from the future BKPRO SAV app via `/api/sav-tickets/webhook`.
 */
export default function AlertesPanel() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [chantiersUrgent, setChantiersUrgent] = useState([]);
  const [team, setTeam] = useState([]);
  const [filter, setFilter] = useState("active");
  const [loading, setLoading] = useState(true);
  const [convertTarget, setConvertTarget] = useState(null);
  const [expandedTimeline, setExpandedTimeline] = useState({});
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const highlightId = params.get("ticket");

  const load = async () => {
    setLoading(true);
    try {
      const [t, c, u] = await Promise.all([
        api.get("/sav-tickets", { params: { status: filter, limit: 100 } }),
        api.get("/chantiers").catch(() => ({ data: [] })),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setTickets(t.data || []);
      setChantiersUrgent((c.data || []).filter((x) => x.status === "urgent"));
      setTeam((u.data || []).filter((x) => x.is_active !== false && x.role !== "external"));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const patch = async (id, patchBody) => {
    try {
      await api.patch(`/sav-tickets/${id}`, patchBody);
      toast.success("Ticket mis à jour");
      emitSavUpdated();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteOne = async (t) => {
    if (!window.confirm(`Supprimer définitivement « ${t.title || "ce ticket"} » ?`)) return;
    try {
      await api.delete(`/sav-tickets/${t.id}`);
      toast.success("Ticket supprimé");
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(t.id);
        return next;
      });
      emitSavUpdated();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === tickets.length) return new Set();
      return new Set(tickets.map((t) => t.id));
    });
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Supprimer définitivement ${ids.length} ticket${ids.length > 1 ? "s" : ""} ?`)) return;
    setBulkDeleting(true);
    try {
      const r = await api.post("/sav-tickets/bulk-delete", { ids });
      toast.success(`${r.data?.deleted || ids.length} ticket${(r.data?.deleted || ids.length) > 1 ? "s" : ""} supprimé${(r.data?.deleted || ids.length) > 1 ? "s" : ""}`);
      setSelectedIds(new Set());
      emitSavUpdated();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBulkDeleting(false); }
  };

  const counts = useMemo(() => ({
    total: tickets.length,
    open: tickets.filter((t) => t.status === "open").length,
    critical: tickets.filter((t) => t.priority === "critical").length,
  }), [tickets]);

  return (
    <div className="p-4 space-y-4" data-testid="alertes-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-red-500/15 border border-red-500/30 flex items-center justify-center">
            <Fire size={20} weight="duotone" className="text-red-400" />
          </div>
          <div>
            <div className="font-display font-bold text-lg">Alertes & SAV</div>
            <div className="text-xs text-white/50">Chantiers urgents · tickets SAV entrants</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SavRatingBadge />
          <div className="flex gap-1.5">
            <StatChip label="Ouverts" value={counts.open} color="#EF4444" />
            <StatChip label="Critiques" value={counts.critical} color="#F59E0B" />
          </div>
        </div>
      </div>

      {chantiersUrgent.length > 0 && (
        <section>
          <div className="text-[10px] uppercase tracking-widest text-red-400 mb-2 flex items-center gap-1.5">
            <Warning size={12} weight="fill" /> Chantiers marqués urgent ({chantiersUrgent.length})
          </div>
          <div className="space-y-2">
            {chantiersUrgent.map((c) => (
              <button
                key={c.id}
                onClick={() => nav(`/chantiers/${c.id}`)}
                className="w-full text-left rounded-xl bg-red-500/10 border border-red-500/30 hover:border-red-500/60 p-3 flex items-center gap-3 transition-colors"
                data-testid={`urgent-chantier-${c.id}`}
              >
                <div className="w-1.5 h-10 rounded-full shrink-0" style={{ background: c.color || "#EF4444" }} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-xs text-white/50 truncate">{c.address || "Adresse non renseignée"}</div>
                </div>
                <ArrowRight size={14} className="text-white/40" />
              </button>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2 flex items-center gap-1.5">
          <LightbulbFilament size={12} weight="duotone" /> Tickets SAV (BKPRO SAV)
        </div>
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1 mb-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`shrink-0 text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === f.key ? "bg-[#005CFF] border-[#005CFF] text-white" : "bg-white/5 border-white/10 text-white/60 hover:border-white/25"}`}
              data-testid={`sav-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Selection toolbar — appears when 1+ ticket is checked */}
        {tickets.length > 0 && (
          <div className="flex items-center justify-between gap-2 mb-2 px-1" data-testid="sav-select-bar">
            <button
              type="button"
              onClick={toggleSelectAll}
              className="inline-flex items-center gap-1.5 text-[11px] text-white/60 hover:text-white active:scale-95 transition"
              data-testid="sav-select-all"
            >
              {selectedIds.size === tickets.length && tickets.length > 0
                ? <CheckSquare size={14} weight="fill" className="text-[#7CB2FF]" />
                : <Square size={14} weight="regular" />
              }
              <span>
                {selectedIds.size > 0
                  ? `${selectedIds.size} sélectionné${selectedIds.size > 1 ? "s" : ""}`
                  : "Tout sélectionner"}
              </span>
            </button>
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-2">
                <button
                  type="button"
                  onClick={() => setSelectedIds(new Set())}
                  className="text-[11px] text-white/50 hover:text-white/80 active:scale-95 transition"
                  data-testid="sav-select-clear"
                >
                  Annuler
                </button>
                <Button
                  size="sm"
                  onClick={bulkDelete}
                  disabled={bulkDeleting}
                  className="h-7 text-xs bg-red-500 hover:bg-red-600 text-white gap-1"
                  data-testid="sav-bulk-delete"
                >
                  <Trash size={12} weight="bold" />
                  {bulkDeleting ? "Suppression…" : `Supprimer (${selectedIds.size})`}
                </Button>
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="h-32 rounded-2xl bg-white/5 animate-pulse" />
        ) : tickets.length === 0 ? (
          <EmptyHint />
        ) : (
          <div className="space-y-2">
            {tickets.map((t) => {
              const s = STATUS_META[t.status] || STATUS_META.open;
              const p = PRIO_META[t.priority || "normal"];
              const highlighted = t.id === highlightId;
              const isSelected = selectedIds.has(t.id);
              return (
                <div
                  key={t.id}
                  className={`rounded-xl border p-3 space-y-2 transition-all ${
                    isSelected
                      ? "border-red-500/60 bg-red-500/[0.08]"
                      : highlighted
                      ? "border-[#005CFF] bg-[#005CFF]/10 shadow-lg shadow-[#005CFF]/20"
                      : "border-white/10 bg-white/[0.03] hover:border-white/25"
                  }`}
                  data-testid={`sav-ticket-${t.id}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Selection checkbox */}
                    <button
                      type="button"
                      onClick={() => toggleSelect(t.id)}
                      className="mt-1 shrink-0 active:scale-90 transition"
                      title={isSelected ? "Désélectionner" : "Sélectionner"}
                      data-testid={`sav-select-${t.id}`}
                    >
                      {isSelected
                        ? <CheckSquare size={18} weight="fill" className="text-red-400" />
                        : <Square size={18} weight="regular" className="text-white/30 hover:text-white/60" />
                      }
                    </button>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: s.color + "22", color: s.color }}>
                      <s.Icon size={15} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{t.title}</span>
                        <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full" style={{ background: p.color + "22", color: p.color }}>{p.label}</span>
                        <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full" style={{ background: s.color + "22", color: s.color }}>{s.label}</span>
                      </div>
                      {t.description && <div className="text-xs text-white/60 mt-1 line-clamp-2">{t.description}</div>}
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-[11px] text-white/50">
                        {t.client_name && <span>{t.client_name}</span>}
                        {t.client_phone && (
                          <a href={`tel:${t.client_phone}`} className="flex items-center gap-1 hover:text-[#7CB2FF]"><Phone size={10} /> {t.client_phone}</a>
                        )}
                        {t.client_email && (
                          <a href={`mailto:${t.client_email}`} className="flex items-center gap-1 hover:text-[#7CB2FF]"><EnvelopeSimple size={10} /> {t.client_email}</a>
                        )}
                        {t.address && <span className="flex items-center gap-1"><MapPin size={10} /> {t.address}</span>}
                        <span className="text-white/30">· {new Date(t.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                    </div>
                    {t.chantier_id && (
                      <button
                        onClick={() => nav(`/chantiers/${t.chantier_id}`)}
                        className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors shrink-0"
                        title="Ouvrir le chantier associé"
                        data-testid={`sav-open-ch-${t.id}`}
                      >
                        <ArrowRight size={14} weight="bold" />
                      </button>
                    )}
                    <button
                      onClick={() => deleteOne(t)}
                      className="p-1.5 rounded-lg bg-white/5 hover:bg-red-500/20 text-white/40 hover:text-red-400 transition-colors shrink-0"
                      title="Supprimer ce ticket"
                      data-testid={`sav-delete-${t.id}`}
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                  <div className="flex gap-1.5 flex-wrap items-center">
                    {/* Assign to tech dropdown */}
                    {t.status !== "resolved" && t.status !== "closed" && (
                      <div className="flex items-center gap-1 text-[11px] text-white/50" data-testid={`sav-assign-wrap-${t.id}`}>
                        <UserCircle size={12} weight="duotone" />
                        <select
                          value={t.assigned_to || ""}
                          onChange={(e) => patch(t.id, { assigned_to: e.target.value || null })}
                          className="h-6 bg-white/5 border border-white/10 rounded px-1.5 text-[11px] text-white focus:outline-none focus:border-[#005CFF]"
                          data-testid={`sav-assign-${t.id}`}
                        >
                          <option value="" className="bg-[#0a0a0a]">Non affecté</option>
                          {team.map((u) => (
                            <option key={u.id} value={u.id} className="bg-[#0a0a0a]">{u.name}</option>
                          ))}
                        </select>
                        {t.assigned_to && team.find((u) => u.id === t.assigned_to) && (
                          <span className="text-emerald-400">✓</span>
                        )}
                      </div>
                    )}
                    {t.status === "open" && (
                      <Button size="sm" className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-black" onClick={() => patch(t.id, { status: "acknowledged" })} data-testid={`sav-ack-${t.id}`}>Marquer vu</Button>
                    )}
                    {(t.status === "open" || t.status === "acknowledged") && (
                      <Button size="sm" className="h-7 text-xs bg-[#005CFF] hover:bg-[#0047CC]" onClick={() => patch(t.id, { status: "in_progress" })} data-testid={`sav-progress-${t.id}`}>Prendre en charge</Button>
                    )}
                    {!t.converted_intervention_id && t.status !== "resolved" && t.status !== "closed" && (
                      <Button size="sm" className="h-7 text-xs bg-violet-500 hover:bg-violet-600 text-white" onClick={() => setConvertTarget(t)} data-testid={`sav-convert-${t.id}`}>
                        <ArrowsClockwise size={11} weight="bold" /> Créer intervention
                      </Button>
                    )}
                    {t.converted_intervention_id && (
                      <span className="text-[10px] px-2 py-1 rounded-full bg-violet-500/15 border border-violet-500/30 text-violet-300 flex items-center gap-1" data-testid={`sav-converted-${t.id}`}>
                        <ArrowsClockwise size={10} weight="bold" /> Intervention créée
                      </span>
                    )}
                    {t.status !== "resolved" && t.status !== "closed" && (
                      <Button size="sm" className="h-7 text-xs bg-emerald-500 hover:bg-emerald-600 text-black" onClick={() => patch(t.id, { status: "resolved" })} data-testid={`sav-resolve-${t.id}`}>Résoudre</Button>
                    )}
                    {(t.status === "resolved" || t.status === "closed") && (
                      <a
                        href={`${API}/sav-tickets/${t.id}/report.pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 h-7 text-xs px-2.5 rounded-md bg-white/10 hover:bg-white/15 border border-white/15 text-white transition-colors"
                        data-testid={`sav-report-pdf-${t.id}`}
                        title="Télécharger le rapport de synthèse SAV"
                      >
                        <FilePdf size={12} weight="duotone" /> Rapport PDF
                      </a>
                    )}
                    {t.report_generated_at && (
                      <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 flex items-center gap-1" title={`Rapport généré le ${new Date(t.report_generated_at).toLocaleString("fr-FR")}${(t.report_sent_to || []).length ? ` · envoyé à ${(t.report_sent_to || []).length} destinataire(s)` : ""}`} data-testid={`sav-report-badge-${t.id}`}>
                        <CheckCircle size={10} weight="fill" /> Rapport envoyé
                      </span>
                    )}
                    {t.status === "resolved" && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs border border-white/15" onClick={() => patch(t.id, { status: "closed" })} data-testid={`sav-close-${t.id}`}>Clore</Button>
                    )}
                    <button
                      type="button"
                      onClick={() => setExpandedTimeline((prev) => ({ ...prev, [t.id]: !prev[t.id] }))}
                      className="ml-auto inline-flex items-center gap-1 h-7 text-[11px] px-2 rounded-md text-white/50 hover:text-white/90 hover:bg-white/5 transition-colors"
                      data-testid={`sav-timeline-toggle-${t.id}`}
                      title="Voir l'historique du ticket"
                    >
                      <Clock size={11} weight="duotone" /> Historique
                      {expandedTimeline[t.id] ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />}
                    </button>
                  </div>
                  {expandedTimeline[t.id] && (
                    <SavTicketTimeline ticketId={t.id} />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <ConvertTicketDialog
        open={!!convertTarget}
        onOpenChange={(o) => !o && setConvertTarget(null)}
        ticket={convertTarget}
        onConverted={() => load()}
      />
    </div>
  );
}

function StatChip({ label, value, color }) {
  return (
    <div className="rounded-xl px-3 py-1.5 border" style={{ background: color + "15", borderColor: color + "40" }}>
      <div className="text-[9px] uppercase tracking-widest" style={{ color }}>{label}</div>
      <div className="font-display font-bold text-lg leading-tight" style={{ color }}>{value}</div>
    </div>
  );
}

function EmptyHint() {
  return (
    <div className="rounded-2xl bg-white/[0.02] border border-dashed border-white/10 p-6 flex items-start gap-3">
      <Info size={18} className="text-[#7CB2FF] shrink-0 mt-0.5" weight="duotone" />
      <div className="text-xs text-white/60 leading-relaxed">
        Aucun ticket dans ce filtre. Cet onglet est prêt à recevoir les tickets SAV de la future application <b>BKPRO SAV</b>.
        <div className="mt-2 text-[10px] font-mono text-white/40">
          Endpoint webhook : <span className="text-white/70">POST /api/sav-tickets/webhook</span> — Header <span className="text-white/70">X-Webhook-Secret</span>
        </div>
      </div>
    </div>
  );
}
